import BigNumber from 'bignumber.js'
import FormValidator from 'config/constants/formValidator'
import { ChainIdEnum } from 'config/constants/network'
import { BIG_ONE } from 'config/constants/number'
import { HUSD_TOKEN } from 'config/constants/tokens'
import { ValidationError } from 'config/types/validator'
import { useAnalytics } from 'hooks/useAnalytics'
import useForm from 'hooks/useForm'
import useMatchBreakpoints from 'hooks/useMatchBreakpoints'
import useModal from 'hooks/useModal'
import Column from 'layout/Components/Column'
import { RowCenter } from 'layout/Components/Row'
import { useMemo, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import BonusService from 'services/BonusService'
import { BalanceResponse } from 'services/types'
import { useAppDispatch, useAppSelector } from 'state'
import { useTokenUsdPrice } from 'state/app/hooks'
import { updateBalances } from 'state/profile/actions'
import { Icons } from 'svgs'
import Box from 'UIKit/Box/Box'
import CircleLoader from 'UIKit/Loader/CircleLoader'
import { getBalanceTokenKey } from 'utils'
import { HunnyToast } from 'utils/toastify'
import ExtraInfo, { ExtraInfoItem } from 'views/Payment/components/ExtraInfo'
import CurrencySelectDropdown from 'views/Payment/DesktopPayment/components/CurrencySelectDropdown'
import { PaymentPageEnums } from 'views/Payment/Menu'
import {
  AmountDisplay,
  PaymentFormControl,
  PaymentFormInputAmount,
  PaymentLabel,
  PaymentStroke,
  StickyButtonWrapper,
  StyledActionButton,
} from 'views/Payment/MobilePayment/components/styles'
import PaymentTokenSelectModal from '../../components/PaymentTokenSelectModal'
import SelectedCurrencyItem from '../../components/SelectedCurrencyItem'
import { useSwapableTokens } from '../../hooks/useSwap'

const SwapInputErrorMessages = {
  [ValidationError.Insufficient]: 'Insufficient balance',
  [ValidationError.LessThan]: 'Value must be greater than {{value}} {{unit}}',
}

interface SwapFormProps {}

const SwapForm: React.FC<SwapFormProps> = () => {
  const { swapableTokens } = useSwapableTokens()
  const { isMobile } = useMatchBreakpoints()

  const [onPresentSelectTokenModal] = useModal(PaymentTokenSelectModal)

  const { t } = useTranslation()
  const { recordEvent } = useAnalytics()
  const HusdTokenAmount = useAppSelector(
    (state) => state.profile.balances[getBalanceTokenKey(HUSD_TOKEN.network, HUSD_TOKEN.code)],
  )
  const parsedHUSDAmount = useMemo(() => new BigNumber(HusdTokenAmount || 0), [HusdTokenAmount])
  const dispatch = useAppDispatch()
  const tokenBalances = useAppSelector((state) => state.profile.balances)

  const { states, controls, validate, isValid } = useForm({
    amount: {
      validators: [FormValidator.lt(0.01), FormValidator.max(parsedHUSDAmount)],
      value: '',
    },
  })

  const [submiting, setSubmiting] = useState<boolean>(false)
  const [selectedToken, setSelectedToken] = useState(swapableTokens[0])
  const tokenUsdPrice = useTokenUsdPrice(selectedToken)

  const isDisabledForm = useMemo(
    () => !isValid || submiting || !states.amount.value,
    [isValid, submiting, states.amount.value],
  )
  const [typedValueFormat, valueFormatPrice] = useMemo(() => {
    const format = new BigNumber(states.amount.value || 0).decimalPlaces(6, BigNumber.ROUND_FLOOR)
    const display = BIG_ONE.div(tokenUsdPrice)
      .times(format || 0)
      .decimalPlaces(6, BigNumber.ROUND_FLOOR)
      .toString(10)

    return [format, display]
  }, [states.amount.value, tokenUsdPrice])

  const handleSwap = async () => {
    validate('amount')

    if (typedValueFormat.gt(parsedHUSDAmount)) return

    setSubmiting(true)

    const updatedBalance: BigNumber = parsedHUSDAmount.minus(typedValueFormat)
    const result = await BonusService.swap(typedValueFormat.toString(10), selectedToken)

    if (result) {
      recordEvent('swap_husd', {
        [ChainIdEnum[selectedToken.network]]: selectedToken.name,
      })
      HunnyToast.success(
        t('Swap successful!'),
        t('Your HUSD has been swapped for {{tokenName}}. Your account balance has been updated.', {
          tokenName: selectedToken.name,
        }),
      )

      dispatch(
        updateBalances({
          data: [{ amount: updatedBalance.toString(), currency: 'HUSD', network: ChainIdEnum.HPN } as BalanceResponse],
        }),
      )
      controls.amount.clear()
    }

    setSubmiting(false)
  }

  const fetchBalance = (_token) => {
    return Promise.resolve(new BigNumber(tokenBalances[getBalanceTokenKey(_token.network, _token.code)] || 0))
  }

  return (
    <Column gap="var(--pm-space-component, 20px)" height="100%">
      <Column gap="var(--pm-space-label, 8px)">
        <PaymentLabel>
          <Trans>Currency</Trans>
        </PaymentLabel>
        <SelectedCurrencyItem selectedToken={HUSD_TOKEN} />
      </Column>
      <Column gap="var(--pm-space-label, 8px)">
        <PaymentFormControl
          state={states.amount}
          label={t('Amount')}
          formatErrorMessage={(errors) =>
            t(SwapInputErrorMessages[errors[0]], {
              value: '0.01',
              unit: 'HUSD',
            })
          }
          disabled={submiting}
        >
          <PaymentFormInputAmount
            tabIndex={1}
            control={controls.amount}
            name="amount"
            disabled={submiting}
            value={states.amount.value}
            token={HUSD_TOKEN}
            errors={!submiting && states.amount.errors}
            validators={controls.amount.validators}
            onErrorChanged={controls.amount.onErrorChanged}
            onValueChanged={controls.amount.onValueChanged}
            max={parsedHUSDAmount}
            getFee={null}
          />
        </PaymentFormControl>

        <AmountDisplay title="Available" amount={parsedHUSDAmount} currency={HUSD_TOKEN} />
      </Column>

      <RowCenter>
        <Icons.ArrowDownAlt2Icon />
      </RowCenter>

      <Column gap="var(--pm-space-label, 8px)">
        {isMobile ? (
          <>
            <PaymentLabel>
              <Trans>Select Coin</Trans>
            </PaymentLabel>
            <SelectedCurrencyItem
              selectedToken={selectedToken}
              hideNetwork={false}
              onPresentSelectCurrency={() =>
                onPresentSelectTokenModal({
                  selectedToken,
                  onCurrencySelect: setSelectedToken,
                  hideNetwork: false,
                  currencies: swapableTokens,
                  allCurrencies: swapableTokens,
                  isAllowCloseAfterSelect: true,
                  from: PaymentPageEnums.Swap,
                  fetchBalance,
                })
              }
            />
          </>
        ) : (
          <CurrencySelectDropdown
            scrollHeight={250}
            value={selectedToken}
            onTokenChange={setSelectedToken}
            hideNetwork={false}
            allCurrencies={swapableTokens}
            options={swapableTokens}
            from={PaymentPageEnums.Swap}
            fetchTokenBalanceFn={fetchBalance}
          />
        )}
      </Column>

      <PaymentStroke />

      <ExtraInfo>
        <ExtraInfoItem title="You will get">
          {states.amount.errors.length === 0 ? (
            <>
              {valueFormatPrice} {selectedToken.name}
            </>
          ) : (
            <>-</>
          )}
        </ExtraInfoItem>
      </ExtraInfo>

      <StickyButtonWrapper>
        <StyledActionButton disabled={isDisabledForm} onClick={handleSwap}>
          {submiting && (
            <Box mr="12px">
              <CircleLoader />
            </Box>
          )}
          <Trans>Swap</Trans>
        </StyledActionButton>
      </StickyButtonWrapper>
    </Column>
  )
}

export default SwapForm
